import classNames from "classnames"
import hotToast, { Toaster } from "react-hot-toast"
import type { Toast as ToastProps } from "react-hot-toast"

import { CircleLoader, IconButton } from "@atoms"
import { ReactComponent as InfoIcon } from "assets/icons/info.svg"
import { ReactComponent as SuccessIcon } from "assets/icons/success.svg"
import { ReactComponent as ErrorIcon } from "assets/icons/error.svg"
import { ReactComponent as WarningIcon } from "assets/icons/warning.svg"
import { ReactComponent as CloseIcon } from "assets/icons/close.svg"

import {
  BaseBody,
  BaseEndIcon,
  BaseContent,
  BaseToast,
  BaseStartIcon,
  BaseTitle,
  BaseToastFooter,
  BaseToastPrimaryAction,
  BaseToastDismissAction,
} from "./styles"
import DismissProvider, { useOnDismiss } from "./context"

interface Props extends ToastProps {
  bold?: boolean
  variant: "success" | "info" | "warning" | "error" | "loading"
}
type Options = Partial<Omit<Props, "variant">>

const ToastContainer: React.FC<React.ComponentProps<typeof Toaster>> = (
  props
) => (
  <Toaster
    gutter={24}
    containerStyle={{ top: "84px", zIndex: 99999 }}
    {...props}
  />
)

const Toast: React.FC<Props> = ({ children, variant, bold, id, visible }) => {
  return (
    <DismissProvider id={id}>
      <BaseToast
        className={classNames(`color--${variant}`, {
          bold,
          visible,
        })}
        data-cy={`toast_${variant}`}
      >
        <BaseStartIcon>
          {variant === "loading" && <CircleLoader />}
          {variant === "info" && <InfoIcon width={20} />}
          {variant === "warning" && <WarningIcon />}
          {variant === "success" && <SuccessIcon width={15} />}
          {variant === "error" && <ErrorIcon />}
        </BaseStartIcon>
        <BaseContent>{children}</BaseContent>
        <BaseEndIcon onClick={() => toast.dismiss(id)}>
          <IconButton>
            <CloseIcon />
          </IconButton>
        </BaseEndIcon>
      </BaseToast>
    </DismissProvider>
  )
}

const ToastTitle: React.FC<React.ComponentPropsWithoutRef<"h5">> = ({
  className = "",
  ...props
}) => <BaseTitle className={classNames("body-heavy", className)} {...props} />

const ToastBody: React.FC<React.ComponentPropsWithoutRef<"p">> = ({
  className = "",
  ...props
}) => <BaseBody className={classNames("caption", className)} {...props} />

const ToastActions = BaseToastFooter

interface ToastPrimaryActionProps
  extends React.ComponentPropsWithoutRef<"span"> {
  dismiss?: boolean
}
const ToastPrimaryAction: React.FC<ToastPrimaryActionProps> = ({
  className,
  dismiss = true,
  onClick,
  ...props
}) => {
  const onDismiss = useOnDismiss()

  return (
    <BaseToastPrimaryAction
      className={classNames("caption-heavy", className)}
      onClick={(...args) => {
        if (dismiss) {
          onDismiss()
        }
        onClick?.(...args)
      }}
      {...props}
    />
  )
}

const ToastDismissAction: React.FC<React.ComponentPropsWithoutRef<"span">> = ({
  className,
  ...props
}) => (
  <BaseToastDismissAction
    className={classNames("caption-heavy", className)}
    onClick={useOnDismiss()}
    {...props}
  />
)

const buildToast = (variant: Props["variant"]) => {
  return (children: React.ReactNode, options?: Options) => {
    return hotToast.custom(
      (props) => {
        return (
          <Toast {...props} bold={options?.bold} variant={variant}>
            {children}
          </Toast>
        )
      },
      {
        position: "top-center",
        duration: variant === "loading" ? 3600000 : 6000,
        ...options,
      }
    )
  }
}

const toast = {
  dismiss: (id?: string) => hotToast.dismiss(id),

  loading: buildToast("loading"),
  info: buildToast("info"),
  success: buildToast("success"),
  warning: buildToast("warning"),
  error: buildToast("error"),
  promise(
    promise: Promise<unknown>,
    render: {
      loading?: React.ReactNode
      success?: React.ReactNode
      error?: React.ReactNode
    },
    options?: Options
  ) {
    let toastId = ""
    if (render.loading) {
      toastId = this.loading(render.loading, options)
    }
    promise
      .then(() => {
        if (toastId) {
          this.dismiss(toastId)
        }
        if (render.success) {
          this.success(render.success, options)
        }
      })
      .catch(() => {
        if (toastId) {
          this.dismiss(toastId)
        }
        if (render.error) {
          this.error(render.error, options)
        }
      })
  },
}

export default toast
export {
  ToastContainer,
  ToastTitle,
  ToastBody,
  ToastActions,
  ToastPrimaryAction,
  ToastDismissAction,
}
